// VictoryProgressHUD — collapsible panel showing progress toward non-conquest
// victory conditions. Each enabled path shows a progress bar tracking how many
// empire ticks the condition has been sustained vs the required threshold.

import { Progress } from '@aod/ui/components/ui/progress';
import { BookOpen, ChevronDown, ChevronUp, Coins, Handshake, Stars } from 'lucide-react';
import { type MessageKey, useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import type { VictoryProgressState } from '../sim/useSimWorld/types.js';
import {
  PanelDockGrip,
  PanelRailButton,
  PanelResizeHandle,
  useIsDesktop,
  usePanelDockStyle,
} from './layout/PanelDockControls.js';
import { usePanelLayout } from './layout/usePanelLayout.js';

interface VictoryProgressHUDProps {
  readonly victoryProgress: VictoryProgressState;
  readonly localPlayerIndex: number;
}

interface PathInfo {
  key: string;
  label: MessageKey;
  icon: React.ReactNode;
  enabled: boolean;
  metAt: number;
}

const ICON_CLS = 'size-3.5 shrink-0';

function buildPaths(vp: VictoryProgressState, playerIndex: number): PathInfo[] {
  const prog = vp.progress[playerIndex];
  const cfg = vp.config;

  return [
    {
      key: 'economy',
      label: 'victory.path.economy',
      icon: <Coins className={ICON_CLS} />,
      enabled: cfg.economyGold > 0 || cfg.economyTradeRoutes > 0,
      metAt: prog?.economyMetAt ?? 0,
    },
    {
      key: 'science',
      label: 'victory.path.science',
      icon: <BookOpen className={ICON_CLS} />,
      enabled: cfg.scienceKnowledge > 0,
      metAt: prog?.scienceMetAt ?? 0,
    },
    {
      key: 'faith',
      label: 'victory.path.faith',
      icon: <Stars className={ICON_CLS} />,
      enabled: cfg.faithTotal > 0 || cfg.faithTemples > 0,
      metAt: prog?.faithMetAt ?? 0,
    },
    {
      key: 'diplomacy',
      label: 'victory.path.diplomacy',
      icon: <Handshake className={ICON_CLS} />,
      enabled: cfg.diplomacyHonor > 0,
      metAt: prog?.diplomacyMetAt ?? 0,
    },
  ];
}

export function VictoryProgressHUD({ victoryProgress, localPlayerIndex }: VictoryProgressHUDProps) {
  const t = useT();
  // Collapse persists in the panel-dock store; the dock field is 'right' but
  // the panel keeps its bespoke (bottom-centre) slot, so defaults look
  // exactly as before the docks existed.
  const collapsed = usePanelLayout((s) => s.panels.victory.collapsed);
  const toggleCollapsed = usePanelLayout((s) => s.toggleCollapsed);
  const dock = usePanelLayout((s) => s.panels.victory.dock);
  const dockStyle = usePanelDockStyle('victory', true);
  const isDesktop = useIsDesktop();
  const paths = buildPaths(victoryProgress, localPlayerIndex);
  const enabledPaths = paths.filter((p) => p.enabled);
  if (enabledPaths.length === 0) return null;

  if (collapsed && isDesktop) {
    return (
      <PanelRailButton
        id="victory"
        label={t('victory.progress')}
        icon={<Stars className="size-4" />}
      />
    );
  }

  const empireTick = victoryProgress.empireTick;
  const sustain = victoryProgress.config.sustainEmpireTicks;

  return (
    <section
      aria-label={t('victory.progress')}
      data-testid="victory-progress"
      {...appDom({
        id: 'victory-progress',
        type: 'Card',
        role: 'card',
        scope: 'main',
        label: t('victory.progress'),
      })}
      style={dockStyle}
      className="absolute top-(--hud-panel-top) left-2 z-10 w-56 rounded-lg border border-border/70 bg-panel-scrim p-3 text-xs text-card-foreground shadow-lg backdrop-blur md:top-auto md:bottom-10 md:left-(--centre-inset)"
    >
      <div className="flex min-h-6 items-center justify-between gap-1">
        <button
          type="button"
          className="flex min-h-6 flex-1 cursor-pointer items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
          onClick={() => toggleCollapsed('victory')}
        >
          <span>{t('victory.progress')}</span>
          {collapsed ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        </button>
        <PanelDockGrip id="victory" />
      </div>

      {!collapsed && (
        <div className="mt-2 space-y-2">
          {enabledPaths.map((p) => {
            const sustained = p.metAt > 0 ? empireTick - p.metAt : 0;
            const pct = Math.min(100, Math.round((sustained / sustain) * 100));

            return (
              <div key={p.key} className="space-y-1">
                <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-1">
                    {p.icon}
                    {t(p.label)}
                  </span>
                  <span className="tabular-nums">
                    {p.metAt > 0 ? `${sustained}/${sustain}` : '—'}
                  </span>
                </div>
                <Progress value={p.metAt > 0 ? pct : 0} className="h-1.5" aria-label={t(p.label)} />
              </div>
            );
          })}
        </div>
      )}
      <PanelResizeHandle id="victory" side={dock === 'bottom' ? 'top' : 'left'} />
    </section>
  );
}
